import { FC, MouseEventHandler, ReactNode } from 'react';
import { cva, VariantProps } from 'class-variance-authority';
import clsx from 'clsx';
import { Heading } from '@/shared/ui/Heading/Heading';

export interface ProfileActionProps extends VariantProps<typeof cvaRoot> {
  className?: string;
  titleClassName?: string;
  subtitleClassName?: string;
  descrClassName?: string;
  icon?: ReactNode;
  title?: string;
  subtitle?: string;
  onClick?: MouseEventHandler<HTMLDivElement>;
  disabled?: boolean;
  descr?: string;
  infoSlot?: ReactNode;
}

const ProfileAction: FC<ProfileActionProps> = ({
  icon,
  title,
  titleClassName,
  subtitleClassName,
  descrClassName,
  subtitle,
  descr,
  infoSlot,
  disabled = false,
  onClick,
  border,
  className,
}) => {
  return (
    <div
      onClick={onClick}
      className={clsx(
        cvaRoot({ border, disabled }),
        className,
        onClick && 'cursor-pointer'
      )}>
      <div className={cvaContainer()}>
        {icon}
        {title ? (
          <Heading
            center
            level={'3'}
            size={'h3'}
            className={clsx(cvaTitle({ disabled }), titleClassName)}>
            {title}
          </Heading>
        ) : null}
        {subtitle ? (
          <Heading
            center
            level={'3'}
            size={'subtitle-lg'}
            className={clsx(cvaSubtitle(), subtitleClassName)}>
            {subtitle}
          </Heading>
        ) : null}
        {descr && <p className={clsx(cvaDescr(), descrClassName)}>{descr}</p>}
      </div>

      {infoSlot}
    </div>
  );
};

const cvaRoot = cva(
  [
    'ProfileAction-cvaRoot',
    'flex flex-col items-center md-max:flex-grow justify-center gap-1.6',
    'h-full',
    'p-2.4 md-max:p-1.6',
    'border-4 rounded-[2.4rem] md-max:rounded-[1.6rem]',
    'duration-300',
  ],
  {
    variants: {
      border: {
        dashed: 'border-dashed',
      },
      disabled: {
        true: '!border-cGray700',
        false: 'border-cPurple900 bg-cBaseBlack',
      },
    },
  }
);

const cvaContainer = cva([
  'ProfileAction-cvaContainer',
  'flex flex-col items-center gap-1.6 md-max:gap-1.2',
  'px-1.2 py-0.6 md-max:px-0.8 md-max:py-0.6 sm-max:py-0.8',
]);

const cvaTitle = cva(
  ['ProfileAction-cvaTitle', 'transition-colors duration-300'],
  {
    variants: {
      disabled: {
        true: 'text-cGray700 !text-2 !font-semibold',
        false: 'text-cPurple500',
      },
    },
  }
);

const cvaSubtitle = cva([
  'ProfileAction-cvaSubtitle',
  'text-center text-2.4 font-semibold',
]);

const cvaDescr = cva([
  'ProfileAction-cvaDescr',
  'text-1.6 text-center text-[#999999]',
]);

export default ProfileAction;
